
import React, { useState } from 'react';
import TeamHeader from '@/components/compare/TeamHeader';
import StatsTable from '@/components/compare/StatsTable';
import FormSection from '@/components/compare/FormSection';
import HeadToHeadResults from '@/components/compare/HeadToHeadResults';
import { Card } from "@/components/ui/card";
import { calculateMatchStats } from '@/utils/teamMatchStats';
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronUp } from "lucide-react";

interface CompareContentProps {
  homeTeam: string;
  awayTeam: string;
  matchData: {
    homeTeamMatches: any[];
    awayTeamMatches: any[];
  };
}

const CompareContent: React.FC<CompareContentProps> = ({ 
  homeTeam, 
  awayTeam, 
  matchData 
}) => {
  const [showForm, setShowForm] = useState(true);
  
  // No conversion of dates - use original format
  const formattedMatchData = {
    homeTeamMatches: matchData.homeTeamMatches,
    awayTeamMatches: matchData.awayTeamMatches
  };
  
  const homeTeamStats = {
    ...calculateMatchStats(formattedMatchData.homeTeamMatches, homeTeam),
    team: homeTeam
  };
  const awayTeamStats = {
    ...calculateMatchStats(formattedMatchData.awayTeamMatches, awayTeam),
    team: awayTeam
  };

  // Filter head-to-head matches for HeadToHeadResults
  const h2hMatches = formattedMatchData.homeTeamMatches.filter(match => 
    (match.HomeTeam === homeTeam && match.AwayTeam === awayTeam) ||
    (match.HomeTeam === awayTeam && match.AwayTeam === homeTeam)
  );

  return (
    <div className="space-y-8">
      <Card className="p-6">
        <div className="grid grid-cols-3 gap-8">
          <TeamHeader team={homeTeam} color="blue" />
          <div className="text-center">
            <h2 className="text-2xl font-bold">vs</h2>
          </div>
          <TeamHeader team={awayTeam} color="red" />
        </div>
      </Card>

      <Card className="p-6">
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-center">Team Comparison</h2>
          <StatsTable 
            homeTeamStats={homeTeamStats} 
            awayTeamStats={awayTeamStats} 
          />
        </div>
      </Card>

      <Card className="p-6">
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="flex-grow text-center">
              <h2 className="text-2xl font-bold">Season Form</h2>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowForm(!showForm)}
              className="flex items-center gap-2"
            >
              <span>{showForm ? 'Hide' : 'Show'}</span>
              {showForm ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>
          </div>
          {showForm && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <FormSection 
                matches={formattedMatchData.homeTeamMatches} 
                teamName={homeTeam}
                isHomeTeam={true}
              />
              <FormSection 
                matches={formattedMatchData.awayTeamMatches} 
                teamName={awayTeam}
                isHomeTeam={false}
              />
            </div>
          )}
        </div>
      </Card>

      <Card className="p-6">
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-center">Head to Head History</h2>
          <HeadToHeadResults 
            matches={h2hMatches}
            homeTeam={homeTeam}
            awayTeam={awayTeam}
          />
        </div>
      </Card>
    </div>
  );
};

export default CompareContent;
